import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  useCallback,
} from 'react';
import HTMLFlipBook from 'react-pageflip';
import './DocumentPage.scss';
import ChervonDown from 'components/panoMenu/iconArrow/ChervonDown';
import { FullScreenIcon } from 'components/icons';
import MinimizeIcon from 'components/icons/MinimizeIcon';
import { useToggleFullscreen } from 'common/hooks/useToggleFullscreen';

const DocumentPage = forwardRef(({ isOpen, onClose, onViewChange }, ref) => {
  // to-gap || mat-bang || ''
  const [type, setType] = useState('');
  const { toggleFullscreen, isFullscreen } = useToggleFullscreen();
  const isMobile = useMemo(() => window.innerWidth <= 1025, []);
  const [viewportWidth, setViewportWidth] = useState(window.innerWidth);
  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const sizeBucket = useMemo(
    () => (viewportWidth < 1025 ? 'sm' : viewportWidth <= 1500 ? 'md' : 'lg'),
    [viewportWidth]
  );
  const flipbook = useRef(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [scale, setScale] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef(null);
  const contentRef = useRef(null);
  const handleZoomIn = () =>
    setScale((s) => Math.min(s + (isMobile ? 0.1 : 0.15), 3));
  const handleZoomOut = () =>
    setScale((s) => Math.max(s - (isMobile ? 0.1 : 0.15), 1));

  const dragStartRef = useRef({ x: 0, y: 0, panX: 0, panY: 0 });
  const onDragStart = (e) => {
    if (scale <= 1) return;
    e.preventDefault();
    e.stopPropagation();
    if (e.pointerId !== undefined && e.currentTarget.setPointerCapture) {
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch (_) {}
    }
    const point = e;
    dragStartRef.current = {
      x: point.clientX,
      y: point.clientY,
      panX: pan.x,
      panY: pan.y,
    };
    setIsDragging(true);
  };

  const onDragMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    e.stopPropagation();
    const point = e;
    const dx = point.clientX - dragStartRef.current.x;
    const dy = point.clientY - dragStartRef.current.y;
    const next = clampPan(
      dragStartRef.current.panX + dx,
      dragStartRef.current.panY + dy
    );
    setPan(next);
  };

  const onDragEnd = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (e.pointerId !== undefined && e.currentTarget.releasePointerCapture) {
      try {
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch (_) {}
    }
  };

  const makePaths = (size, arr) =>
    arr.map((r) => `/assets/images/documents/${size}/${r}`);

  const flipBookData = {
    'to-gap': {
      name: 'Tờ Gấp Jasari Prime',
      width: isFullscreen ? 600 : isMobile ? 350 : 450,
      aspectRatio: 335 / 500,
      isPortrait: isMobile,
      showCover: false,
      dataSm: makePaths('sm', [
        'togap/page_1.jpg',
        'togap/page_2.jpg',
        'togap/page_3.jpg',
        'togap/page_4.jpg',
        'togap/page_6.jpg',
        'togap/page_7.jpg',
        'togap/page_5.jpg',
        'togap/page_8.jpg',
      ]),
      dataMd: makePaths('md', [
        'togap/page_1.jpg',
        'togap/page_2.jpg',
        'togap/page_3.jpg',
        'togap/page_4.jpg',
        'togap/page_6.jpg',
        'togap/page_7.jpg',
        'togap/page_5.jpg',
        'togap/page_8.jpg',
      ]),
      dataLg: makePaths('lg', [
        'togap/page_1.jpg',
        'togap/page_2.jpg',
        'togap/page_3.jpg',
        'togap/page_4.jpg',
        'togap/page_6.jpg',
        'togap/page_7.jpg',
        'togap/page_5.jpg',
        'togap/page_8.jpg',
      ]),
    },
    'mat-bang-tang': {
      width: isFullscreen ? 750 : isMobile ? 350 : 900,
      aspectRatio: 500 / 365,
      isPortrait: true,
      showCover: false,
      name: 'Mặt Bằng Tầng Jasari Prime',
      dataSm: makePaths('sm', [
        'matbangtang/page_1.jpg',
        'matbangtang/page_2.jpg',
        'matbangtang/page_3.jpg',
        'matbangtang/page_10.jpg',
        'matbangtang/page_5.jpg',
        'matbangtang/page_6.jpg',
        'matbangtang/page_7.jpg',
        'matbangtang/page_8.jpg',
        'matbangtang/page_9.jpg',
        'matbangtang/page_4.jpg',
        'matbangtang/page_11.jpg',
        'matbangtang/page_12.jpg',
        'matbangtang/page_13.jpg',
        'matbangtang/page_14.jpg',
        'matbangtang/page_15.jpg',
        'matbangtang/page_16.jpg',
        'matbangtang/page_17.jpg',
        'matbangtang/page_18.jpg',
        'matbangtang/page_19.jpg',
        'matbangtang/page_20.jpg',
        'matbangtang/page_21.jpg',
        'matbangtang/page_22.jpg',
        'matbangtang/page_23.jpg',
        'matbangtang/page_24.jpg',
      ]),
      dataMd: makePaths('md', [
        'matbangtang/page_1.jpg',
        'matbangtang/page_2.jpg',
        'matbangtang/page_3.jpg',
        'matbangtang/page_10.jpg',
        'matbangtang/page_5.jpg',
        'matbangtang/page_6.jpg',
        'matbangtang/page_7.jpg',
        'matbangtang/page_8.jpg',
        'matbangtang/page_9.jpg',
        'matbangtang/page_4.jpg',
        'matbangtang/page_11.jpg',
        'matbangtang/page_12.jpg',
        'matbangtang/page_13.jpg',
        'matbangtang/page_14.jpg',
        'matbangtang/page_15.jpg',
        'matbangtang/page_16.jpg',
        'matbangtang/page_17.jpg',
        'matbangtang/page_18.jpg',
        'matbangtang/page_19.jpg',
        'matbangtang/page_20.jpg',
        'matbangtang/page_21.jpg',
        'matbangtang/page_22.jpg',
        'matbangtang/page_23.jpg',
        'matbangtang/page_24.jpg',
      ]),
      dataLg: makePaths('lg', [
        'matbangtang/page_1.jpg',
        'matbangtang/page_2.jpg',
        'matbangtang/page_3.jpg',
        'matbangtang/page_10.jpg',
        'matbangtang/page_5.jpg',
        'matbangtang/page_6.jpg',
        'matbangtang/page_7.jpg',
        'matbangtang/page_8.jpg',
        'matbangtang/page_9.jpg',
        'matbangtang/page_4.jpg',
        'matbangtang/page_11.jpg',
        'matbangtang/page_12.jpg',
        'matbangtang/page_13.jpg',
        'matbangtang/page_14.jpg',
        'matbangtang/page_15.jpg',
        'matbangtang/page_16.jpg',
        'matbangtang/page_17.jpg',
        'matbangtang/page_18.jpg',
        'matbangtang/page_19.jpg',
        'matbangtang/page_20.jpg',
        'matbangtang/page_21.jpg',
        'matbangtang/page_22.jpg',
        'matbangtang/page_23.jpg',
        'matbangtang/page_24.jpg',
      ]),
    },
  };

  const bookData = flipBookData?.[type];
  const selectedData =
    sizeBucket === 'sm'
      ? bookData?.dataSm
      : sizeBucket === 'md'
      ? bookData?.dataMd
      : bookData?.dataLg;
  const bookDataSelected = bookData
    ? { ...bookData, data: selectedData }
    : undefined;

  const clampPan = useCallback((nx, ny) => {
    const viewport = containerRef.current?.getBoundingClientRect();
    const content = contentRef.current?.getBoundingClientRect();
    if (!viewport || !content) return { x: nx, y: ny };

    const vw = viewport.width;
    const vh = viewport.height;
    const sw = content.width;
    const sh = content.height;

    const maxX = Math.max(0, (sw - vw) / 2);
    const maxY = Math.max(0, (sh - vh) / 2);

    return {
      x: Math.max(-maxX, Math.min(maxX, nx)),
      y: Math.max(-maxY, Math.min(maxY, ny)),
    };
  }, []);

  useEffect(() => {
    // Notify parent component about the current view type
    if (onViewChange) {
      onViewChange(type);
    }
  }, [type, onViewChange]);

  useEffect(() => {
    setCurrentPage(0);
    setScale(1);
  }, [type]);

  useEffect(() => {
    setScale(1);
  }, [isFullscreen]);

  useEffect(() => {
    if (scale === 1) setPan({ x: 0, y: 0 });
  }, [scale]);

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    backToList: handleBackToList,
    getCurrentType: () => type,
  }));

  useEffect(() => {
    return () => {
      setType('');
    };
  }, []);

  if (!isOpen) return null;

  const handleOpenFile = (fileName) => {
    setType(fileName);
  };

  const handleBackToList = () => {
    setType('');
  };

  const safeFlipPrev = () => {
    const flip = flipbook.current?.pageFlip?.();
    if (flip?.flipPrev) flip.flipPrev();
  };

  const safeFlipNext = () => {
    const flip = flipbook.current?.pageFlip?.();
    if (flip?.flipNext) flip.flipNext();
  };

  return (
    <div
      className={`document-page ${
        isFullscreen ? 'document-page--fullscreen' : ''
      }`}
      style={{
        width: `${
          document.body.classList.contains('menu-open') && !isMobile
            ? 'calc(100vw - 260px)'
            : '100vw'
        }`,
        height: `${isMobile ? 'calc(100dvh - 50px)' : '100dvh'}`,
      }}
    >
      {type && bookData ? (
        <>
          <div className="flipbook-actions">
            <button
              className={`flipbook-prev ${currentPage === 0 ? 'disabled' : ''}`}
              onClick={safeFlipPrev}
            >
              <ChervonDown size={12} />
            </button>
            <button
              className={`flipbook-next ${
                currentPage >= bookDataSelected?.data?.length - 3
                  ? 'disabled'
                  : ''
              }`}
              onClick={safeFlipNext}
            >
              <ChervonDown />
            </button>
          </div>

          <div className="container" ref={containerRef}>
            <div className="container-actions">
              {!isMobile && (
                <button
                  className="pdf-modal__full-btn"
                  onClick={() => {
                    toggleFullscreen();
                    setCurrentPage(0);
                  }}
                  title="Fullscreen"
                >
                  {isFullscreen ? (
                    <MinimizeIcon color="#fff" />
                  ) : (
                    <FullScreenIcon />
                  )}
                </button>
              )}
              {!isFullscreen && (
                <button
                  className="pdf-modal__back-btn"
                  onClick={() => setType('')}
                  title="Quay lại danh sách"
                >
                  ×
                </button>
              )}
            </div>
            <div
              className="flipbook-scale"
              ref={contentRef}
              style={{
                transform: `translate(${pan.x}px, ${pan.y}px) scale(${scale})`,
                willChange: 'transform',
                pointerEvents: scale > 1 ? 'none' : 'auto',
              }}
            >
              <Book
                book={bookDataSelected}
                isMobile={isMobile}
                ref={flipbook}
                onFlip={(e) => {
                  setCurrentPage(e.data);
                }}
                key={isFullscreen ? 'fullscreen' : 'normal'}
              />
            </div>
            {scale > 1 && (
              <div
                className={`pan-overlay ${isDragging ? 'dragging' : ''}`}
                onPointerDown={onDragStart}
                onPointerMove={onDragMove}
                onPointerUp={onDragEnd}
                onPointerCancel={onDragEnd}
              />
            )}
            <div className="zoom-controls">
              <button
                className="zoom-btn zoom-out"
                onClick={handleZoomOut}
                title="Thu nhỏ"
              >
                −
              </button>
              <button
                className="zoom-btn zoom-in"
                onClick={handleZoomIn}
                title="Phóng to"
              >
                +
              </button>
            </div>
          </div>
        </>
      ) : (
        <div className="document-container">
          <div className="document__title">
            <div className="logo">
              <img
                src="/assets/khp-images/tour/e055c252-77cb-488d-a256-4a2c0fc46cf2.jpg"
                alt="logo"
              />
            </div>
            <h2 className="document__subtitle">TÀI LIỆU DỰ ÁN</h2>
          </div>

          <div className="document__list">
            <div
              className="document"
              data-type="to-gap"
              onClick={() => handleOpenFile('to-gap')}
            >
              <div className="backdrop" />
              <div className="content">
                <img src="/assets/images/doc-icon1.webp" alt="Pdf icon" />
                <div className="document-name">Tờ gấp</div>
              </div>
            </div>
            <div
              className="document"
              data-type="mat-bang-tang"
              onClick={() => handleOpenFile('mat-bang-tang')}
            >
              <div className="backdrop" />
              <div className="content">
                <img src="/assets/images/doc-icon2.webp" alt="Pdf icon" />
                <div className="document-name">Mặt bằng tầng</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default DocumentPage;

const Book = forwardRef(({ book, onFlip, isMobile }, ref) => {
  return (
    <HTMLFlipBook
      width={book.width}
      height={book.width / book.aspectRatio}
      maxShadowOpacity={0.5}
      drawShadow={true}
      ref={ref}
      size="fixed"
      showCover={book.showCover}
      usePortrait={book.isPortrait}
      onFlip={onFlip}
    >
      {/* <div className="page" style={{ background: 'transparent' }}>
        <div className="page-content cover">
          <img
            src="https://upload.wikimedia.org/wikipedia/commons/9/98/International_Pok%C3%A9mon_logo.svg"
            alt="Pokémon Logo"
            className="pokemon-logo"
          />
        </div>
      </div> */}

      {book.data.map((page, index) => (
        <div className="page" key={page}>
          <div className="page-content">
            <div className="page-container">
              <img src={book.data[index]} alt={page} />
            </div>
            <span
              className={`page-corner ${index % 2 === 0 ? 'left' : 'right'}`}
            >
              {index + 1}/{book.data.length}
            </span>
          </div>
        </div>
      ))}
    </HTMLFlipBook>
  );
});
